import { StrictMode, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from '@tanstack/react-router';
import type { MemberKind } from '@buildmate/core';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import './styles.css';
import { createPorts, selectedAdapter } from './bootstrap.ts';
import { PortsProvider } from './lib/ports-context.tsx';
import { SessionProvider, useSessionState } from './lib/session-context.tsx';
import { buildRouter } from './router.tsx';

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 30_000, retry: 1 } },
});

// Route guards run outside React, so they read the session through this
// mutable holder, which SessionProvider keeps current. One source of truth,
// reachable from both worlds.
let currentStatus: 'loading' | 'signed-out' | 'no-company' | 'ready' = 'loading';
/**
 * The kind of the active membership, for the guards that split the internal
 * app from the portals. A sub signing in has to land on their own surface:
 * the internal app would show them navigation the rules deny anyway.
 */
let currentKind: MemberKind | null = null;
const router = buildRouter({ getStatus: () => currentStatus, getKind: () => currentKind });

function RoutedApp() {
  const state = useSessionState();

  useEffect(() => {
    const previous = currentStatus;
    const previousKind = currentKind;
    currentStatus = state.status;
    currentKind = state.status === 'ready' ? state.session.membership.kind : null;
    // Re-run the guards whenever sign-in state changes, so signing out moves
    // the user off a protected route rather than leaving them on a dead page.
    // The kind matters too: switching company can change which surface the
    // same person belongs on.
    if (previous !== state.status || previousKind !== currentKind) void router.invalidate();
  }, [state]);

  // Mounted straight away, even while the session is still resolving.
  // Holding the router back until the session was ready meant the browser
  // painted a bare "Loading…" and nothing else until Firebase Auth had
  // initialised and read IndexedDB — measured at 2.6 s to first contentful
  // paint on the portal with every byte already delivered (P4-25).
  // `RequireSession` now holds back the individual pages that need a
  // session, so the frame is on screen while that happens, and the guards
  // re-run when the status settles.
  return <RouterProvider router={router} />;
}

const container = document.getElementById('root');
if (!container) throw new Error('No #root element');

const ports = await createPorts();
// eslint-disable-next-line no-console
console.info(`[buildmate] data adapter: ${selectedAdapter()}`);

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <PortsProvider ports={ports}>
        <SessionProvider>
          <RoutedApp />
        </SessionProvider>
      </PortsProvider>
    </QueryClientProvider>
  </StrictMode>,
);
